import React, { useState } from "react";
import api, { formatTL, formatDuration, formatDurationDays, isMultiDay, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { useLiveQuery, TAGS, invalidate } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plate } from "@/components/Plate";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PayMethodDialog from "@/components/PayMethodDialog";
import { BusinessTagSelect, BusinessBadge, BusinessQuotaStrip, BusinessQuickTag } from "@/components/BusinessTag";
import {
  CheckCircle, ArrowRight, ImageSquare, Gift,
} from "@phosphor-icons/react";

export async function waiveAsGuest(s) {
  if (!window.confirm(`${s.plate} misafir olarak eklensin mi? Ücret ${formatTL(s.fee)} sıfırlanacak.`)) return;
  try {
    await api.post(`/sessions/${s.id}/waive`, { reason: "Misafir muafiyeti" });
    toast.success(`${s.plate} misafir olarak eklendi — ücret sıfırlandı`);
    invalidate([TAGS.SESSIONS, TAGS.UNPAID]);
  } catch (e) { toast.error(e?.response?.data?.detail || "Muaf edilemedi"); }
}

function timeAgo(iso) {
  if (!iso) return "";
  const diffS = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffS < 60) return `${diffS}sn önce`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}dk önce`;
  return `${Math.floor(diffS / 3600)}sa önce`;
}

export default function Live() {
  const [lot, setLot] = useState("all");
  const [payFor, setPayFor] = useState(null);

  const { data: rows } = useLiveQuery(
    TAGS.SESSIONS,
    async () => {
      const q = lot === "all" ? "" : `?lot=${lot}`;
      return (await api.get(`/sessions/active${q}`)).data;
    },
    { intervalMs: 5000, deps: [lot] },
  );

  const { data: justExited } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.UNPAID],
    async () => (await api.get("/sessions/just-exited?minutes=30&limit=10")).data,
    { intervalMs: 3000 },
  );

  const list = rows || [];
  const unpaidExits = (justExited || []).filter((s) => s.payment_status === "bekliyor");

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Canlı</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Otoparktaki Araçlar</h1>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
            <span className="pulse-dot" /> {list.length} aktif · her 5sn · tarife değişikliklerini anlık yansıtır
          </div>
        </div>
        <Select value={lot} onValueChange={setLot}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="live-lot-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Otoparklar</SelectItem>
            <SelectItem value="ust_kat">Üst Kat</SelectItem>
            <SelectItem value="alt_kat">Alt Kat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <BusinessQuotaStrip />

      {/* Just-exited hero panel */}
      {unpaidExits.length > 0 && (
        <Card className="rounded-sm border-2 border-primary bg-primary/5" data-testid="live-just-exited-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="pulse-dot bg-primary" />
                <h3 className="font-bold uppercase tracking-widest text-sm">
                  Az Önce Çıktı — Tahsilat Bekliyor ({unpaidExits.length})
                </h3>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Her 3sn güncellenir
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {unpaidExits.map((s) => (
                <ExitedCard key={s.id} s={s} onPay={() => setPayFor(s)} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plaka</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Giriş</TableHead>
                <TableHead>Süre</TableHead>
                <TableHead className="text-right">Anlık Ücret</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aktif araç yok</TableCell></TableRow>
              )}
              {list.map((s) => (
                <TableRow key={s.id} data-testid={`live-row-${s.plate}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Plate country={s.country_code}>{s.plate}</Plate>
                      <BusinessBadge session={s} />
                      <BusinessTagSelect session={s} compact />
                    </div>
                  </TableCell>
                  <TableCell className="uppercase text-xs tracking-widest">
                    {s.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(s.entry_time)}</TableCell>
                  <TableCell className="font-mono">
                    <div className="flex items-center gap-2">
                      {formatDuration(s.current_duration_minutes)}
                      {isMultiDay(s.current_duration_minutes) && (
                        <Badge data-testid={`live-multiday-${s.plate}`}
                               className="rounded-sm bg-red-600 hover:bg-red-600 text-white text-[10px] uppercase tracking-widest border-transparent font-sans">
                          {formatDurationDays(s.current_duration_minutes)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-primary" data-testid={`live-fee-${s.plate}`}>
                    {s.subscriber_free ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-emerald-600 text-sm font-bold">MUAF</span>
                        {s.exempt_reason && (
                          <span className="text-[10px] text-emerald-700/80 font-normal uppercase tracking-wider">
                            {s.exempt_reason}
                          </span>
                        )}
                      </div>
                    ) : formatTL(s.running_fee)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PayMethodDialog
        session={payFor}
        open={!!payFor}
        onOpenChange={(o) => !o && setPayFor(null)}
        onDone={() => setPayFor(null)}
      />
    </div>
  );
}

function ExitedCard({ s, onPay }) {
  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center bg-white rounded-sm border border-primary/30 p-3"
         data-testid={`live-exited-${s.plate}`}>
      <div className="flex gap-1 items-center">
        <PhotoTile src={s.entry_image_url} label="GİRİŞ" />
        <ArrowRight size={14} className="text-muted-foreground" />
        <PhotoTile src={s.exit_image_url} label="ÇIKIŞ" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Plate country={s.country_code}>{s.plate}</Plate>
          <BusinessBadge session={s} />
          <Badge className="rounded-sm text-[9px] uppercase tracking-widest bg-primary/10 text-primary border-transparent">
            {s.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}
          </Badge>
          <span className="text-[10px] text-muted-foreground uppercase">{timeAgo(s.exit_time)}</span>
        </div>
        <div className="text-[11px] text-muted-foreground font-mono">
          {formatDateTime(s.entry_time)} → {formatDateTime(s.exit_time)}
        </div>
        <div className="text-xs text-muted-foreground">Süre: {formatDuration(s.duration_minutes)}</div>
        <BusinessQuickTag session={s} className="mt-1.5" />
      </div>
      <div className="text-right min-w-[120px]">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Alınacak</div>
        <div className="text-2xl font-bold font-mono text-primary leading-none" data-testid={`live-exited-fee-${s.plate}`}>
          {formatTL(s.fee)}
        </div>
        <Button size="sm" onClick={onPay} data-testid={`live-pay-${s.plate}`}
                className="mt-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-[10px] font-bold">
          <CheckCircle size={12} weight="fill" /> Ödendi
        </Button>
        <Button size="sm" variant="outline" onClick={() => waiveAsGuest(s)} data-testid={`live-guest-${s.plate}`}
                className="mt-1 ml-1 rounded-sm uppercase tracking-widest text-[10px] font-bold">
          <Gift size={12} /> Misafir
        </Button>
      </div>
    </div>
  );
}

function PhotoTile({ src, label }) {
  const [failed, setFailed] = React.useState(false);
  const showImg = src && !failed;
  return (
    <div className="relative w-14 h-14 rounded-sm overflow-hidden bg-muted border border-border shrink-0">
      {showImg ? (
        <img src={src} alt={label} className="w-full h-full object-cover"
             onError={() => setFailed(true)} />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-0.5">
          <ImageSquare size={20} />
          <span className="text-[7px] uppercase tracking-widest opacity-60">Yok</span>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[8px] uppercase tracking-widest text-center">
        {label}
      </div>
    </div>
  );
}
